<template>
  <!-- Prevent prettier to remove parentheses from v-model type cast -->
  <!-- prettier-ignore -->
  <div>
    <div class="flex flex-col md:flex-row">
      <RevCard
        :class="[
          'mr-[0.1rem] flex items-start rounded-b-none px-16 pt-24 md:rounded-b-lg md:grow md:rounded-r-none md:p-32',
          { 'rounded-b-none md:rounded-b-none': isPanelOpen },
        ]"
      >
        <RevIllustration
          v-if="product.image"
          alt=""
          class="hidden md:mr-[1.6rem] md:block"
          :height="48"
          :src="product.image"
          :width="48"
        />
        <div class="mb-24 flex flex-col gap-16 overflow-hidden md:mb-0">
          <RevLink
            class="text-static-default-mid body-1-bold no-underline md:underline"
            :to="quotationsLink"
          >
            {{ product.title }}
          </RevLink>
          <div class="flex flex-col gap-16 md:flex-row md:gap-24">
            <div class="flex">
              <p class="text-static-default-mid body-2 mr-12">
                {{ i18n(translations.marketLabel) }}
              </p>
              <div class="flex gap-8 px-8 md:items-baseline">
                <Flag :country-code="(quote.marketCode as MarketCountryCode)" />
                <p class="text-static-default-mid body-2-bold">
                  {{ quote.marketCode }}
                </p>
              </div>
            </div>
            <div
              class="flex flex-col items-baseline justify-between gap-y-8 md:flex-row md:justify-normal md:gap-24"
            >
              <p class="text-static-default-mid body-2 flex-[2] md:mt-0">
                {{ i18n(translations.idLabel) }} <span>{{ product.productId }}</span>
              </p>
              <RevButtonBase
                v-if="showTurnAllOffline"
                class="text-static-default-mid body-2 cursor-pointer bg-transparent underline md:mt-0"
                @click="handleTurnAllOffline"
              >
                {{ i18n(translations.turnOffline) }}
              </RevButtonBase>
            </div>
          </div>
          <div class="flex flex-col gap-12 md:flex-row md:flex-wrap">
            <template
              v-for="group in listingsGroups"
              :key="group.title"
            >
              <div v-if="group.listings.length > 0" class="flex shrink grow basis-0 gap-12">
                <p class="text-static-default-low body-2 md:text-nowrap">
                  {{ group.title }}:
                </p>
                <div class="flex flex-col gap-12 md:flex-row md:flex-wrap">
                  <RevTag
                    v-for="listing in group.listings"
                    :key="generateListingKey(listing)"
                    class="self-start"
                    :label="getQuoteGradeName(listing.grade)"
                    :variant="getTagVariant(group.id)"
                  />
                </div>
              </div>
            </template>
          </div>
        </div>
      </RevCard>
      <RevCard
        :class="[
          'border-static-default-mid flex rounded-t-none border-t-2 py-20 md:rounded-tr-lg md:hover:bg-float-default-low-hover sm:px-24 md:rounded-l-none md:border-t-0 md:p-0 md:px-16',
          { 'rounded-b-none md:rounded-b-none': isPanelOpen },
        ]"
      >
        <RevButtonBase
          class="flex w-full cursor-pointer items-center justify-end bg-transparent md:justify-center md:px-8 "
          data-test="offer-dropdown-cta"
          @click="toggleProductDetails"
        >
          <p class="text-static-default-mid body-1 grow text-left md:hidden">
            {{ i18n(translations.seeDetails) }}
          </p>
          <IconChevronDown :class="{ 'rotate-180': isPanelOpen }" />
        </RevButtonBase>
      </RevCard>
    </div>
    <Transition
      appear
      enter-active-class="transition-opacity delay-100 duration-100 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-100 ease-out"
      leave-to-class="opacity-0"
    >
      <RevCard
        v-if="isPanelOpen"
        class="flex flex-col gap-32 rounded-t-none p-[2rem] md:p-24"
      >
        <template
          v-for="group in listingsGroups"
          :key="`${group.title}-${group.listings.length}`"
        >
          <div v-if="group.listings.length > 0">
            <p class="text-static-default-hi body-1-bold mb-16">
              {{ group.title }}
            </p>
            <QuotesListingLine
              v-for="(listing, index) in group.listings"
              :key="generateListingKey(listing)"
              :index
              :is-price-to-win-loading="isPriceToWinLoading && listing.state === LISTING_PUBLICATION_STATE.OFFLINE"
              :listing
              :loading-grades
              :market-code="quote.marketCode"
              :pricing-estimation="getPricingEstimationByGrade(pricingEstimations, listing.grade)"
              :product-id="product.productId"
              :tag-variant="getTagVariant(group.id)"
              @save-price="handleUpdateListing"
              @toggle-state="handleUpdateListing"
            />
          </div>
        </template>
      </RevCard>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { MarketCountryCode } from '@backmarket/http-api'
import {
  type BuyBackQuote,
  BuyBackQuoteListingStates,
  type BuyBackQuotePricingEstimation,
  type PostBuyBackQuoteListing,
} from '@backmarket/http-api/src/api-specs-buyback/seller/buyback-quotes.types'
import { LISTING_PUBLICATION_STATE } from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher.constants'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevCard } from '@ds/components/Card'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { RevTag, type Variants } from '@ds/components/Tag'
import { VariantsEnum } from '@ds/components/Tag/Tag.constant'
import { IconChevronDown } from '@ds/icons/IconChevronDown'

import { ROUTE_NAMES } from '~/constants/routes'
import { useGetPricingEstimations } from '~/scopes/buyback/composables/useGetPricingEstimation'
import { getPricingEstimationByGrade } from '~/scopes/buyback/utils/buyback.utils'

import {
  type BuyBackListingsGroup,
  GROUP_IDS,
  type GroupIds,
  type LoadingItem,
} from '../../QuotesWishList.types'
import {
  generateListingKey,
  getQuoteGradeName,
} from '../../QuotesWishList.utils'

import translations from './OfferDetails.translations'
import { groupListingsByStatus } from './QuotesOfferDetails.adapter'
import QuotesListingLine from './components/ListingLine/QuotesListingLine.vue'

const props = defineProps<{
  loadingItem: LoadingItem
  quote: BuyBackQuote
}>()

const emit = defineEmits<{
  (event: 'update-listing', payload: PostBuyBackQuoteListing[]): void
}>()

const i18n = useI18n()
const isPanelOpen = ref(false)
const isPriceToWinLoading = ref(false)

const listingsGroups = ref<BuyBackListingsGroup[]>([])
const pricingEstimations = ref<BuyBackQuotePricingEstimation[]>([])
listingsGroups.value = groupListingsByStatus(props.quote.listings)

const product = computed(() => props.quote.product)

const showTurnAllOffline = computed(() => {
  const onlineGroups = listingsGroups.value.filter((group) => {
    const groupIsOnline =
      group.id === GROUP_IDS.WITH_BACKBOX ||
      group.id === GROUP_IDS.WITHOUT_BACKBOX
    const groupHasListings = group.listings.length > 0

    return groupIsOnline && groupHasListings
  })

  return onlineGroups.length > 0
})

const quotationsLink = computed(() => ({
  name: ROUTE_NAMES.BUYBACK.CREATE_QUOTATION,
  params: {
    productId: product.value.productId,
  },
  query: { countryCode: props.quote.marketCode },
}))

watch(
  () => props.quote,
  (newQuote) => {
    listingsGroups.value = groupListingsByStatus(newQuote.listings)
  },
  { deep: true },
)

const loadingGrades = computed(() =>
  product.value.productId === props.loadingItem.productId
    ? props.loadingItem.grades
    : [],
)

async function toggleProductDetails() {
  if (isPanelOpen.value) {
    isPanelOpen.value = false

    return
  }

  isPanelOpen.value = true

  const offlineListings = props.quote.listings.filter(
    (listing) => listing.state === LISTING_PUBLICATION_STATE.OFFLINE,
  )

  if (offlineListings.length === 0) {
    return
  }

  const offlineGrades = offlineListings.map((listing) => listing.grade)

  isPriceToWinLoading.value = true
  pricingEstimations.value = await useGetPricingEstimations(
    offlineGrades,
    props.quote.product.productId,
    props.quote.marketCode,
  )

  isPriceToWinLoading.value = false
}

function handleTurnAllOffline() {
  const onlineListings = props.quote.listings.filter(
    (listing) => listing.state === LISTING_PUBLICATION_STATE.ONLINE,
  )

  const listings = onlineListings.map((listing) => {
    return {
      grade: listing.grade,
      state: BuyBackQuoteListingStates.DISABLED,
    }
  })

  emit('update-listing', listings)
}

function handleUpdateListing(listing: PostBuyBackQuoteListing) {
  emit('update-listing', [listing])
}

function getTagVariant(groupId: GroupIds): Variants {
  const tagVariantMap: Record<GroupIds, VariantsEnum> = {
    [GROUP_IDS.WITHOUT_BACKBOX]: VariantsEnum.Danger,
    [GROUP_IDS.WITH_BACKBOX]: VariantsEnum.Success,
    [GROUP_IDS.OFFLINE]: VariantsEnum.Secondary,
  }

  return tagVariantMap[groupId]
}
</script>
